/**
 * App links (#105): which in-app place a web address opened on the phone maps to.
 *
 * iOS (Associated Domains) and Android (App Links) hand the app the full
 * `https://play.grundoapp.com/...` address when a shared link is tapped and the
 * app is installed. Only the two shareable kinds are ours to open — anything
 * else stays with the browser's default behaviour (`null`).
 *
 * The share page's „open in the app" button on iOS uses the app's own scheme
 * (`grundo://s/<code>`) instead, because Messenger's and Facebook's in-app
 * browsers never pass an https link on to the app.
 */
export type AppLinkTarget = { kind: 'activity'; id: string } | { kind: 'share'; code: string };

/** The hosts serving the web app; links on other hosts are not ours. */
const APP_HOSTS = new Set(['play.grundoapp.com', 'grundo.web.app']);
/** `Info.plist` → CFBundleURLSchemes; the server's `IOS_APP_SCHEME`. */
const APP_SCHEME = 'grundo:';

export function appLinkTarget(url: string): AppLinkTarget | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  let path: string;
  if (parsed.protocol === 'https:' && APP_HOSTS.has(parsed.hostname)) path = parsed.pathname;
  // `grundo://s/<code>`: the first segment parses as the host.
  else if (parsed.protocol === APP_SCHEME) path = `/${parsed.hostname}${parsed.pathname}`;
  else return null;
  const activity = /^\/aktivitas\/([A-Za-z0-9_-]{1,128})\/?$/.exec(path);
  if (activity) return { kind: 'activity', id: activity[1]! };
  const share = /^\/s\/([A-Za-z0-9_-]{12})\/?$/.exec(path);
  if (share) return { kind: 'share', code: share[1]! };
  return null;
}
